import type React from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { WebsiteFilter } from "app/pages/keyword-research/keyword-analysis/pages/serp-players/FiltersSection/WebsiteFilter";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { ALL } from "app/common/services/categoryService";
import { DomainsFilter } from "pro-features/components/DomainsFilter/DomainsFilter";
import type { IDomains } from "pro-features/components/DomainsFilter/Types";
import fetchService from "app/common/services/salesFetchService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import { CategoryFilter } from "./CategoryFilter";
import { FiltersContainersWrapper, ChipdownItem } from "./styledComponents";
import type { IFiltersSectionProps, IPageFilters } from "./Types";
import { tableFilterChange } from "./Utils";

export const FiltersSection: React.FC<IFiltersSectionProps> = ({ domainsAPI, categoriesAPI, activationSets }) => {
    const { params } = useCurrentStateAndParams();
    const { category, funcFlag, country, duration, webSource, key, selectedPageTab, keyword, domains } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const [, setSelectedFilters] = useState<Partial<IPageFilters>>({});
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);
    const keywordGroupForApi = isKeywordGroup ? keywordGroupsTransforms.getGroupIdFromUrlParam(keyword) : keyword;
    const deviceType = webSourceToDeviceMap[webSource];

    const handleTableFilterChange = (filter: Partial<IPageFilters>) => {
        setSelectedFilters(tableFilterChange(filter));
    };
    const handleWebsiteFilterOpened = (isOpened: boolean) => {
        if (isOpened) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Website Types/filter opened");
        }
    };

    const apiParams = {
        country,
        webSource,
        to,
        from,
        isWindow,
        keys: keywordGroupForApi,
        selectedPageTab,
        Device: deviceType,
        category: category === ALL ? undefined : category,
        funcFlag,
        domains,
        sourceType: SourceTypeMap[selectedPageTab],
    };

    const { data } = useQuery({
        queryKey: [
            domainsAPI,
            to,
            from,
            duration,
            webSource,
            isWindow,
            key,
            country,
            selectedPageTab,
            keywordGroupForApi,
        ],
        queryFn: async () => fetchService.get(domainsAPI, apiParams),
        select: (response: IDomains[]) => {
            return response;
        },
    });

    return (
        <FiltersContainersWrapper alignItems="center">
            <ChipdownItem data-automation="domains-filter-wrapper">
                <DomainsFilter
                    domainsFilterData={data}
                    activationSets={activationSets}
                />
            </ChipdownItem>
            <ChipdownItem data-automation="website-filter-wrapper">
                <WebsiteFilter
                    onFilterChange={handleTableFilterChange}
                    onDropdownToggle={handleWebsiteFilterOpened}
                    selectedWebsiteFilter={funcFlag}
                />
            </ChipdownItem>
            <ChipdownItem data-automation="category-filter-wrapper">
                <CategoryFilter
                    categoriesAPI={categoriesAPI}
                    activationSets={activationSets}
                />
            </ChipdownItem>
        </FiltersContainersWrapper>
    );
};
